import { voiceConfigStore } from '@/voice/state';
// Settings changed by asking (collaboration v1, PR 5 §4). The assistant's
// approved update_my_settings action returns the new settings and the values
// it replaced, including interface language when explicitly approved. The
// legacy language proposal still needs its own Apply click; a proposal alone
// never changes settings. The server's execution receipt reaches App before
// answer text or audio, so the next spoken reply uses the approved preferences.
// The ordinary tool-output hook uses the same account-bound consumer. Loading a chat's
// history never replays it, so an old chat never flips the theme. A resumed
// stream replays the turn from its start: a result this page already applied
// is ignored, and one it has not (after a reload) is never taken from the
// tool's snapshot, which may be older than the server's settings, so the page
// re-reads the server instead. The tool's card offers Undo: a person's click
// that PUTs `previous` from the browser; the model is not involved. A change
// carries the account it was made for, and it is applied, and undoable, only
// by that account: a reader of someone else's chat sees the line and nothing
// more.
import {
  useCallback,
  useEffect,
  useRef,
  useSyncExternalStore,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  fetchServerSettings,
  normalizeSettings,
  putServerSettings,
  type Settings,
  type SettingsPatch,
  type ThemeChoice,
} from '@/settings';
import type { ToolResultOrigin } from './workspace-chat-transport';
import {
  INITIAL_SETTINGS_CONTROL,
  planChange,
  priorAfterReread,
  readStillFresh,
  saveStillOpen,
  settingsControlReducer,
  undoPrior,
  type SettingsControlAction,
} from './account-settings-state';

export const SETTINGS_TOOL = 'update_my_settings';
/** The keys a person may change by asking (src/company-workspace/control/catalog.mjs). */
export const ASKED_SETTING_KEYS = [
  'theme',
  'language',
  'timezone',
  'chat_model_choice',
  'personality',
  'voice_prefs',
] as const;
export type AskedSettingKey = (typeof ASKED_SETTING_KEYS)[number];
type Asked = Pick<Settings, AskedSettingKey>;

/** An app-language switch the tool proposed; only the person's Apply writes it. */
export interface SettingsProposal {
  /** A BCP-47 tag, or null for automatic. */
  language: string | null;
}

export interface SettingsChange {
  /** The account whose settings changed (the tool's `account`), or null when unknown. */
  account: string | null;
  /** The server's values of the askable keys after the change. */
  settings: Asked;
  /** What the changed keys held before it. */
  previous: SettingsPatch;
  /** The keys the approved action wrote; empty for a legacy proposal alone. */
  changed: AskedSettingKey[];
  /** The app-language switch waiting for the person's Apply, or null. */
  proposed: SettingsProposal | null;
}

function pick(settings: Settings, keys: readonly AskedSettingKey[]): Partial<Asked> {
  return Object.fromEntries(keys.map((key) => [key, settings[key]])) as Partial<Asked>;
}

const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const proposalFrom = (value: Record<string, unknown>): SettingsProposal | null => {
  const proposed = value.status === 'needs_confirmation' ? record(value.proposed) : null;
  return proposed && Object.hasOwn(proposed, 'language')
    ? { language: normalizeSettings(proposed).language }
    : null;
};

/**
 * A successful update_my_settings result (applied, or with an app-language
 * switch that needs the person's confirmation), or null for anything else.
 */
export function settingsChangeFromTool(toolName: string, output: unknown): SettingsChange | null {
  const value = toolName === SETTINGS_TOOL ? record(output) : null;
  const settings = record(value?.settings);
  if (!value || !settings) return null;
  const proposed = proposalFrom(value);
  if (value.status !== 'ok' && !proposed) return null;
  const previous = record(value.previous) ?? {};
  const changed = ASKED_SETTING_KEYS.filter((key) => Object.hasOwn(previous, key));
  return {
    account: typeof value.account === 'string' && value.account ? value.account : null,
    settings: pick(normalizeSettings(settings), ASKED_SETTING_KEYS) as Asked,
    previous: {
      ...pick(normalizeSettings(previous), changed),
      ...(record(previous.voice_prefs)
        ? { voice_prefs: previous.voice_prefs as SettingsPatch['voice_prefs'] }
        : {}),
    },
    changed,
    proposed,
  };
}

/** Merge only the approved fields; a receipt snapshot must not undo newer preferences. */
export function withSettingsChange(current: Settings, change: SettingsChange): Settings {
  const patch = Object.fromEntries(change.changed.map((key) => [key, change.settings[key]]));
  if (change.changed.includes('voice_prefs')) {
    const base = current.voice_prefs ?? change.settings.voice_prefs;
    patch.voice_prefs = base ? { ...base, ...voiceChangePatch(change) } : undefined;
  }
  return { ...current, ...patch };
}

function voiceChangePatch(change: SettingsChange) {
  return Object.fromEntries(
    Object.keys(change.previous.voice_prefs ?? {}).map((key) => [
      key,
      change.settings.voice_prefs?.[key as keyof NonNullable<Settings['voice_prefs']>],
    ]),
  );
}

/** The live voice session observes the same narrow patch as Settings. */
function adoptVoiceChange(change: SettingsChange) {
  const current = voiceConfigStore.current().voice?.prefs;
  if (current && change.changed.includes('voice_prefs'))
    voiceConfigStore.adoptPrefs({ ...current, ...voiceChangePatch(change) });
}

/** Whether the change was made to this signed-in account (never true when either is unknown). */
export const isOwnChange = (change: SettingsChange, userId: string | null) =>
  userId !== null && change.account === userId;

/** Whether the change is still what the person has (Undo is offered only then). */
export const isCurrentChange = (change: SettingsChange, settings: Settings) =>
  change.changed.length > 0 &&
  change.changed.every((key) =>
    key === 'voice_prefs'
      ? Object.keys(change.previous.voice_prefs ?? {}).every((name) => {
          const pref = name as keyof NonNullable<Settings['voice_prefs']>;
          return (
            JSON.stringify(settings.voice_prefs?.[pref]) ===
            JSON.stringify(change.settings.voice_prefs?.[pref])
          );
        })
      : settings[key] === change.settings[key],
  );

/** Whether the person already has the proposed app language (Apply is offered only when not). */
export const hasProposal = (proposal: SettingsProposal, settings: Settings) =>
  settings.language === proposal.language;

export interface SettingsArtifactRoute {
  /** Whether the turn still belongs to the signed-in person who sent it. */
  isOwner: () => boolean;
  /** App's apply (useAccountSettingsControl): it takes only this account's change. */
  apply: (change: SettingsChange, origin: ToolResultOrigin) => unknown;
}

/**
 * The chat transport's first step for a tool result (ChatPane's artifact
 * hook). Settings belong to the account, not the thread: an update_my_settings
 * result goes to App while the turn still belongs to the person who sent it,
 * even if the chat was left, and to nothing else. True when the result was a
 * settings change, so the caller stops there; false hands it on to the
 * workspace's own handling.
 */
export function routeSettingsArtifact(
  toolName: string,
  output: unknown,
  origin: ToolResultOrigin,
  route: SettingsArtifactRoute,
): boolean {
  const change = settingsChangeFromTool(toolName, output);
  if (!change) return false;
  if (route.isOwner()) route.apply(change, origin);
  return true;
}

/**
 * The PUT that undoes a change. A theme that was not chosen on the server
 * before (null) is restored to what this device showed before the change —
 * an explicit value, so every device returns to it — or, when that is not
 * known (the change reached this page only after a reload had already shown
 * it), to the other of light and dark.
 */
export function undoPatch(
  change: SettingsChange,
  priorLocalTheme: ThemeChoice | null,
): SettingsPatch {
  const patch: SettingsPatch = { ...change.previous };
  if (change.changed.includes('theme') && !change.previous.theme)
    patch.theme = priorLocalTheme ?? (change.settings.theme === 'light' ? 'dark' : 'light');
  return patch;
}

/** State of an app-language proposal shown to the person. */
export type ProposalView = 'ask' | 'applied' | 'dismissed' | 'offered';

export interface AccountSettingsControl {
  /** The signed-in account these settings belong to. */
  userId: string;
  settings: Settings;
  /** Read current server settings after an authenticated card action. */
  refresh: () => void;
  /** Adopt a server-confirmed settings action result for this account. */
  apply: (change: SettingsChange, origin: ToolResultOrigin) => unknown;
  /** A person's Undo of the change a tool call made: PUT its `previous` and adopt the result. */
  undo: (change: SettingsChange, toolCallId: string | undefined) => Promise<void>;
  /** A person's Apply of a proposed app language: PUT it and adopt the result. */
  applyProposal: (proposal: SettingsProposal) => Promise<void>;
}

// One module store (App publishes, the cards read), like the Inbox's: it keeps
// App's layout untouched and every card reads the same current settings.
let published: AccountSettingsControl | null = null;
const listeners = new Set<() => void>();
export function publishAccountSettings(next: AccountSettingsControl | null) {
  published = next;
  for (const listener of listeners) listener();
}
export function refreshAccountSettings() {
  published?.refresh();
}
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
/** The signed-in person's settings and Undo, or null outside a signed-in App. */
export const useAccountSettings = () =>
  useSyncExternalStore(
    subscribe,
    () => published,
    () => null,
  );

/** Whether two settings hold the same values (a re-read that changed nothing is not adopted). */
export function sameSettings(a: Settings, b: Settings) {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)] as (keyof Settings)[]);
  return [...keys].every((key) => JSON.stringify(a[key]) === JSON.stringify(b[key]));
}

/** Run `refresh` whenever the page becomes visible again, while `enabled`. */
export function useRefreshWhenVisible(enabled: boolean, refresh: () => void) {
  const latest = useRef(refresh);
  latest.current = refresh;
  useEffect(() => {
    if (!enabled) return;
    const onChange = () => {
      if (document.visibilityState === 'visible') latest.current();
    };
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, [enabled]);
}

export interface AccountSettingsOptions {
  userId: string | null;
  settings: Settings;
  setSettings: Dispatch<SetStateAction<Settings>>;
  theme: { choice: ThemeChoice; adopt: (choice: ThemeChoice | null | undefined) => void };
  /** App's session boundary: whether a late answer still belongs to this account. */
  isCurrent: (userId: string | null) => () => boolean;
}

/**
 * App's account settings: adopt (server values, the theme included; a null
 * theme keeps this device's choice), choose a theme (apply and save; a failed
 * save puts this device back and rejects, so the Appearance field can say so),
 * apply a live update_my_settings change made to this account, Undo, Apply of
 * a proposed app language, and
 * re-read the settings when the page becomes visible again. Publishes the
 * account, its settings, Undo and Apply for the change cards. What each step may do
 * is decided by the pure reducer in ./account-settings-state.ts; this hook
 * dispatches its actions and runs the effects.
 */
export function useAccountSettingsControl(options: AccountSettingsOptions) {
  const latest = useRef(options);
  latest.current = options;
  const state = useRef(INITIAL_SETTINGS_CONTROL);
  const dispatch = useCallback((action: SettingsControlAction) => {
    state.current = settingsControlReducer(state.current, action);
    return state.current;
  }, []);
  const adopt = useCallback(
    (next: Settings) => {
      dispatch({ type: 'adopted' });
      latest.current.setSettings(next);
      latest.current.theme.adopt(next.theme);
      if (next.voice_prefs) voiceConfigStore.adoptPrefs(next.voice_prefs);
    },
    [dispatch],
  );
  /** Read the server's settings; `then` gets them only if nothing moved here meanwhile. */
  const reread = useCallback((then: (fresh: Settings) => void) => {
    const { userId, isCurrent } = latest.current;
    if (!userId) return;
    const current = isCurrent(userId);
    const startedAt = state.current.revision;
    void fetchServerSettings().then((fresh) => {
      if (fresh && current() && readStillFresh(state.current, startedAt)) then(fresh);
    });
  }, []);
  const choose = useCallback(
    async (choice: ThemeChoice) => {
      const { theme, userId, isCurrent, setSettings } = latest.current;
      const before = theme.choice;
      const { openSave: save } = dispatch({ type: 'chose', saving: Boolean(userId) });
      theme.adopt(choice);
      if (!userId || save === null) return;
      const current = isCurrent(userId);
      try {
        const saved = await putServerSettings({ theme: choice });
        if (current()) setSettings(saved);
      } catch (error) {
        // Not saved, so it would not follow the person anywhere: this device
        // goes back to what it showed, unless a newer choice or value came since.
        if (current() && saveStillOpen(state.current, save)) theme.adopt(before);
        throw error;
      } finally {
        dispatch({ type: 'saveSettled', save });
      }
    },
    [dispatch],
  );
  /** Take up a change made to this account (planChange says how); true when it was taken up. */
  const apply = useCallback(
    (change: SettingsChange, origin: ToolResultOrigin) => {
      const { theme, setSettings, userId } = latest.current;
      const plan = planChange(state.current, change, origin, {
        own: isOwnChange(change, userId),
        shown: theme.choice,
      });
      if (plan.kind === 'ignore') return false;
      const { toolCallId } = origin;
      if (plan.kind === 'reread') {
        dispatch({ type: 'took', toolCallId, prior: null });
        reread((fresh) => {
          const prior = priorAfterReread(change, fresh, latest.current.theme.choice);
          if (prior) dispatch({ type: 'took', toolCallId, prior });
          adopt(fresh);
        });
        return true;
      }
      dispatch({ type: 'took', toolCallId, prior: plan.prior });
      dispatch({ type: 'adopted' });
      setSettings((current) => withSettingsChange(current, change));
      if (change.changed.includes('theme')) theme.adopt(change.settings.theme);
      adoptVoiceChange(change);
      return true;
    },
    [adopt, dispatch, reread],
  );
  const undo = useCallback(
    async (change: SettingsChange, toolCallId: string | undefined) => {
      const current = latest.current.isCurrent(latest.current.userId);
      const restored = await putServerSettings(
        undoPatch(change, undoPrior(state.current, toolCallId)),
      );
      if (current()) adopt(restored);
    },
    [adopt],
  );
  // The same path as Undo: the person's click PUTs the proposal and the page
  // adopts the server's answer, so the interface switches at once.
  const applyProposal = useCallback(
    async (proposal: SettingsProposal) => {
      const current = latest.current.isCurrent(latest.current.userId);
      const saved = await putServerSettings({ language: proposal.language });
      if (current()) adopt(saved);
    },
    [adopt],
  );
  const refresh = useCallback(() => {
    reread((fresh) => {
      if (!sameSettings(fresh, latest.current.settings)) adopt(fresh);
    });
  }, [reread, adopt]);
  useRefreshWhenVisible(Boolean(options.userId), refresh);
  useEffect(() => {
    publishAccountSettings(
      options.userId
        ? {
            userId: options.userId,
            settings: options.settings,
            refresh,
            apply,
            undo,
            applyProposal,
          }
        : null,
    );
  }, [options.settings, options.userId, refresh, apply, undo, applyProposal]);
  useEffect(() => () => publishAccountSettings(null), []);
  return { adopt, choose, apply, undo, applyProposal };
}
